import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';
import { LuPalette } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { getMasterFeatures } from '@/utils/api';
import {
	createFeature,
	noOverlapOffset,
	deleteLayerByCustomId,
	getTrainingDataStyle,
	getPointRadius,
} from '@utils/map/helpers';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';
import TrainingDataPalette from '../modifyLayers/ModifyTrainingData';
import { uniqueValuesBy } from '@utils/helpers';

// Local Style Constants
const STROKE_WIDTH = 2;

/** Local Style For Training Data Layer */
const getLayerStyle = classInfo => ({
	'stroke-color': getTrainingDataStyle(classInfo),
	'stroke-width': STROKE_WIDTH,
	'stroke-offset': noOverlapOffset(STROKE_WIDTH),
	'fill-color': getTrainingDataStyle(classInfo, 0.2),
});

/* This component is responsible for rendering the area of interest layer and toggling its visibility. */
const TrainingDataLayer = ({ layerId = 'TrainingDataLayer' }) => {
	const [searchParams] = useSearchParams();
	const defaultVisible = searchParams.get('showTrainingData') === 'true';
	const {
		mapObject,
		project,
		pickedTask,
		defaultFormat,
		colorOptions,
		trainingDataClassNameInfo,
		setActiveSidebar,
		dispatch,
	} = useProject();

	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const [layer, setLayer] = useState(null);
	const [features, setFeatures] = useState([]);
	const { data, error } = useQuery({
		queryKey: ['fetchTrainingData', projectUuid, modelUuid],
		queryFn: () => getMasterFeatures(projectUuid, modelUuid, 'annotation_polygon'),
		enabled: !!projectUuid && !!modelUuid,
		refetchInterval: 60_000 * 5, // 5min
		refetchIntervalInBackground: true,
	});

	const { features: rawFeatures = [] } = data ?? {};

	useEffect(() => {
		const layerInstance = new WebGLVectorLayer({
			name: 'Training Data',
			zIndex: 12,
			visible: defaultVisible,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
			style: {},
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);
		return () => {
			deleteLayerByCustomId(mapObject, layerId);
			setLayer(null);
		};
	}, []);

	useEffect(() => {
		if (error) return console.error('Error fetching training data layer: ', error);

		const featuresOL = rawFeatures.reduce((acc, { geometry, properties }) => {
			const feature = createFeature(geometry, properties);
			if (feature) acc.push(feature);
			return acc;
		}, []);

		setFeatures(featuresOL);

		const classNameInfo = {};
		uniqueValuesBy(rawFeatures, ({ properties }) => properties.classname).forEach(
			(className, index) => {
				classNameInfo[className] = {
					color: colorOptions[index % Object.keys(colorOptions).length],
					visible: true,
				};
			}
		);
		dispatch({
			type: 'setTrainingDataClassNameInfo',
			payload: classNameInfo,
		});
		return () => {
			setFeatures([]);
			dispatch({
				type: 'setTrainingDataClassNameInfo',
				payload: {},
			});
		};
	}, [data]);

	useEffect(() => {
		if (!layer) return;
		const style = getLayerStyle(trainingDataClassNameInfo);
		const visibleFeatures = features.filter(
			feature => trainingDataClassNameInfo[feature.get('classname')]?.visible
		);

		layer.setStyle(style);
		layer.setSource(new VectorSource({ features: visibleFeatures, format: defaultFormat }));
	}, [features, trainingDataClassNameInfo]);

	if (!layer || !data?.features) return null;
	return (
		<li>
			<LayerSettings
				id={layerId}
				layer={layer}
				label={layer?.get('name')}
				defaultState={layer?.getVisible()}
				onCheck={() => layer?.setVisible(true)}
				onUncheck={() => layer?.setVisible(false)}
				layerFunctions={[
					{
						title: 'Palette',
						icon: <LuPalette />,
						onClick: () =>
							setActiveSidebar({
								sidebarId: 'trainingDataPalette',
								parentId: 'layerView',
							}),
					},
				]}
			/>
			<TrainingDataPalette />
		</li>
	);
};
export default TrainingDataLayer;
